import React from 'react';

// Iter 136 — Accessible date picker built from three dropdowns
// (Day / Month / Year) so users never fight a mini-calendar to reach a
// different month or year. Emits 'YYYY-MM-DD' via onChange.
const MONTHS = ['January','February','March','April','May','June','July',
                'August','September','October','November','December'];

function daysInMonth(y, m) { return new Date(y, m, 0).getDate(); }

export function DateDropdownPicker({ value, onChange, minDate, yearsBack = 0, yearsForward = 2, testId = 'date-dropdown' }) {
  const today = new Date();
  const cur = value ? {
    y: parseInt(value.slice(0, 4), 10),
    m: parseInt(value.slice(5, 7), 10),
    d: parseInt(value.slice(8, 10), 10),
  } : { y: '', m: '', d: '' };

  const years = [];
  for (let y = today.getFullYear() - yearsBack; y <= today.getFullYear() + yearsForward; y++) years.push(y);

  const set = (patch) => {
    // '' fields must fall back to today — use || so empty strings don't
    // block date assembly (selecting ANY one dropdown yields a full date).
    const y = patch.y ?? (cur.y || today.getFullYear());
    const m = patch.m ?? (cur.m || today.getMonth() + 1);
    let d = patch.d ?? (cur.d || today.getDate());
    d = Math.min(d, daysInMonth(y, m));
    const str = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    if (minDate && str < minDate) { onChange(minDate); return; }
    onChange(str);
  };

  const maxDay = cur.y && cur.m ? daysInMonth(cur.y, cur.m) : 31;
  const isDayDisabled = (day) => {
    if (!minDate || !cur.y || !cur.m) return false;
    const str = `${cur.y}-${String(cur.m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return str < minDate;
  };

  return (
    <div className="flex gap-2" data-testid={testId}>
      <select className="input flex-[0.9]" value={cur.d || ''}
              onChange={e => set({ d: parseInt(e.target.value, 10) })}
              data-testid={`${testId}-day`}>
        <option value="" disabled>Day</option>
        {Array.from({ length: maxDay }, (_, i) => i + 1).map(day => (
          <option key={day} value={day} disabled={isDayDisabled(day)}>{day}</option>
        ))}
      </select>
      <select className="input flex-[1.4]" value={cur.m || ''}
              onChange={e => set({ m: parseInt(e.target.value, 10) })}
              data-testid={`${testId}-month`}>
        <option value="" disabled>Month</option>
        {MONTHS.map((name, i) => {
          const m = i + 1;
          const disabled = minDate && cur.y
            ? `${cur.y}-${String(m).padStart(2, '0')}-${String(daysInMonth(cur.y, m)).padStart(2, '0')}` < minDate
            : false;
          return <option key={m} value={m} disabled={disabled}>{name}</option>;
        })}
      </select>
      <select className="input flex-1" value={cur.y || ''}
              onChange={e => set({ y: parseInt(e.target.value, 10) })}
              data-testid={`${testId}-year`}>
        <option value="" disabled>Year</option>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}
